import type {
  DeleteOpsVpsConfigResponse,
  ListOpsVpsConfigsResponse,
  ListOpsVpsResponse,
  OpsVpsAutomationSettingsResponse,
  OpsVpsConfigResponse,
  OpsVpsLbActionResponse,
  OpsVpsNodeDetailWithTrend,
  PatchOpsVpsAutomationSettingsRequest,
  UpsertOpsVpsConfigRequest,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

export const VPS_QUERY_KEY = ['vps'] as const;
export const VPS_CONFIGS_QUERY_KEY = ['vps', 'configs'] as const;

export async function listVpsNodes(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOpsVpsResponse> {
  return opsApiRequest<ListOpsVpsResponse>('/v1/ops/vps', { signal }, fetchImpl);
}

export function useVpsNodes(): UseQueryResult<ListOpsVpsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: VPS_QUERY_KEY,
    queryFn: ({ signal }) => listVpsNodes(fetchImpl, signal),
    refetchInterval: 30_000,
  });
}

export async function listVpsConfigs(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<ListOpsVpsConfigsResponse> {
  return opsApiRequest<ListOpsVpsConfigsResponse>('/v1/ops/vps/configs', { signal }, fetchImpl);
}

export async function createVpsConfig(
  payload: UpsertOpsVpsConfigRequest,
  fetchImpl: typeof fetch,
): Promise<OpsVpsConfigResponse> {
  return opsApiRequest<OpsVpsConfigResponse>(
    '/v1/ops/vps/configs',
    { method: 'POST', body: payload },
    fetchImpl,
  );
}

export async function updateVpsConfig(
  id: string,
  payload: UpsertOpsVpsConfigRequest,
  fetchImpl: typeof fetch,
): Promise<OpsVpsConfigResponse> {
  return opsApiRequest<OpsVpsConfigResponse>(
    `/v1/ops/vps/configs/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: payload },
    fetchImpl,
  );
}

export async function deleteVpsConfig(
  id: string,
  fetchImpl: typeof fetch,
): Promise<DeleteOpsVpsConfigResponse> {
  return opsApiRequest<DeleteOpsVpsConfigResponse>(
    `/v1/ops/vps/configs/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    fetchImpl,
  );
}

export function useVpsConfigs(): UseQueryResult<ListOpsVpsConfigsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: VPS_CONFIGS_QUERY_KEY,
    queryFn: ({ signal }) => listVpsConfigs(fetchImpl, signal),
  });
}

export function useCreateVpsConfig(): UseMutationResult<
  OpsVpsConfigResponse,
  Error,
  UpsertOpsVpsConfigRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => createVpsConfig(payload, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: VPS_CONFIGS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: VPS_QUERY_KEY });
    },
  });
}

export function useUpdateVpsConfig(): UseMutationResult<
  OpsVpsConfigResponse,
  Error,
  { id: string; payload: UpsertOpsVpsConfigRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updateVpsConfig(id, payload, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: VPS_CONFIGS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: VPS_QUERY_KEY });
    },
  });
}

export function useDeleteVpsConfig(): UseMutationResult<
  DeleteOpsVpsConfigResponse,
  Error,
  string
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteVpsConfig(id, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: VPS_CONFIGS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: VPS_QUERY_KEY });
    },
  });
}

export async function getVpsNodeDetail(
  id: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsVpsNodeDetailWithTrend> {
  return opsApiRequest<OpsVpsNodeDetailWithTrend>(
    `/v1/ops/vps/${encodeURIComponent(id)}`,
    { signal },
    fetchImpl,
  );
}

export function useVpsNodeDetail(id: string | null): UseQueryResult<OpsVpsNodeDetailWithTrend> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['vps', 'node', id ?? ''],
    queryFn: ({ signal }) => getVpsNodeDetail(id ?? '', fetchImpl, signal),
    enabled: id !== null,
  });
}

export type VpsLbVerb = 'join' | 'enable' | 'disable' | 'remove';

// Wire types are canonical in @nadoutong/contracts (shared with the
// platform route); aliased here so outcome renames/additions surface
// as compile errors in this workspace instead of silent runtime misses.
export type VpsLbOutcome = OpsVpsLbActionResponse['outcome'];

export type VpsLbActionResponse = OpsVpsLbActionResponse;

export interface VpsLbActionInput {
  readonly nodeId: string;
  readonly poolId: string;
  readonly verb: VpsLbVerb;
}

export async function vpsLbAction(
  input: VpsLbActionInput,
  fetchImpl: typeof fetch,
): Promise<VpsLbActionResponse> {
  return opsApiRequest<VpsLbActionResponse>(
    `/v1/ops/vps/${encodeURIComponent(input.nodeId)}/lb/${encodeURIComponent(input.poolId)}/${input.verb}`,
    { method: 'POST' },
    fetchImpl,
  );
}

export function useVpsLbAction(): UseMutationResult<VpsLbActionResponse, Error, VpsLbActionInput> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => vpsLbAction(input, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: VPS_QUERY_KEY });
    },
  });
}

export const VPS_AUTOMATION_SETTINGS_QUERY_KEY = ['vps', 'automation-settings'] as const;

export async function getVpsAutomationSettings(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<OpsVpsAutomationSettingsResponse> {
  return opsApiRequest<OpsVpsAutomationSettingsResponse>(
    '/v1/ops/vps/automation-settings',
    { signal },
    fetchImpl,
  );
}

export function useVpsAutomationSettings(): UseQueryResult<OpsVpsAutomationSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: VPS_AUTOMATION_SETTINGS_QUERY_KEY,
    queryFn: ({ signal }) => getVpsAutomationSettings(fetchImpl, signal),
  });
}

export async function updateVpsAutomationSettings(
  payload: PatchOpsVpsAutomationSettingsRequest,
  fetchImpl: typeof fetch,
): Promise<OpsVpsAutomationSettingsResponse> {
  return opsApiRequest<OpsVpsAutomationSettingsResponse>(
    '/v1/ops/vps/automation-settings',
    { method: 'PATCH', body: payload },
    fetchImpl,
  );
}

export function useUpdateVpsAutomationSettings(): UseMutationResult<
  OpsVpsAutomationSettingsResponse,
  Error,
  PatchOpsVpsAutomationSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => updateVpsAutomationSettings(payload, fetchImpl),
    onSuccess: (data) => {
      qc.setQueryData(VPS_AUTOMATION_SETTINGS_QUERY_KEY, data);
    },
  });
}
